/*
 * DobPicker - reusable branded date-of-birth picker (#12).
 *
 * Replaces the native <input type="date"> whose iOS wheel seeded the day to
 * "today" when only month/year were changed. This renders a compact field
 * (MM-DD-YYYY display) that opens a branded three-column scroll wheel
 * (Month / Day / Year) in a bottom sheet. Each column is its own snap
 * scroller, so there is no cross-column day seeding.
 *
 * Storage contract: emits ISO "YYYY-MM-DD" via onChange (drop-in for the old
 * input; all validators and formatDob consumers expect ISO). Display is
 * MM-DD-YYYY on the closed field. Nothing is written until "Done".
 *
 * TEST 2.25 (P5 step 16): `minAge` (default 0). With it set, the year column starts at
 * nowY - minAge, it opens on 1990, and "Done" refuses a full date under minAge: nothing is
 * written, the sheet stays open and says why (dob.tooYoung, translated). Only the account
 * holder's own DOB passes minAge={18} (OnboardingWelcome, ProfileIdentification and the
 * ID-card confirm step in CardScanner); the insurance subscriber's DOB does not. The rule
 * is utils/age.js, the same one the save handlers use for the server's dob_under_18.
 */
import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { Calendar } from 'lucide-react'
import { useI18n } from '../i18n'
import { ageVerdict, dobYears, dobDefaultYearIndex } from '../utils/age'

const H = 34
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function daysInMonth(mIdx, year) {
  return new Date(year, mIdx + 1, 0).getDate()
}
function pad2(n) {
  return String(n).padStart(2, '0')
}
function parseISO(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '')
  if (!m) return null
  return { y: +m[1], mo: +m[2], d: +m[3] }
}

export default function DobPicker({ value, onChange, label, placeholder = 'Select date', minAge = 0 }) {
  const { t } = useI18n()
  const nowY = new Date().getFullYear()
  const YEARS = useMemo(() => dobYears(nowY, minAge), [nowY, minAge])
  const defaultYearIdx = dobDefaultYearIndex(YEARS)

  const [open, setOpen] = useState(false)
  const [refusal, setRefusal] = useState('')
  const [dayCount, setDayCount] = useState(31)

  const moRef = useRef(null)
  const daRef = useRef(null)
  const yrRef = useRef(null)

  const parsed = parseISO(value)
  const display = parsed ? `${pad2(parsed.mo)}-${pad2(parsed.d)}-${parsed.y}` : ''

  const idxOf = (el) => (el ? Math.round(el.scrollTop / H) : 0)

  const paint = (el) => {
    if (!el) return
    const s = idxOf(el)
    el.querySelectorAll('[data-dobitem]').forEach((it, i) => {
      it.className = 'dobitem' + (i === s ? ' sel' : (Math.abs(i - s) === 1 ? ' adj' : ''))
    })
  }

  const openIndices = useCallback(() => {
    const p = parseISO(value)
    if (p) {
      const yIdx = YEARS.indexOf(p.y)
      return { mo: p.mo - 1, d: p.d - 1, y: yIdx >= 0 ? yIdx : defaultYearIdx }
    }
    return { mo: 0, d: 0, y: defaultYearIdx }
  }, [value, YEARS, defaultYearIdx])

  useEffect(() => {
    if (!open) return
    setRefusal('')
    const { mo, d, y } = openIndices()
    setDayCount(daysInMonth(mo, YEARS[y]))
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        if (moRef.current) moRef.current.scrollTop = mo * H
        if (yrRef.current) yrRef.current.scrollTop = y * H
        if (daRef.current) daRef.current.scrollTop = d * H
        paint(moRef.current)
        paint(daRef.current)
        paint(yrRef.current)
      })
      daRef.current && (daRef.current._raf2 = raf2)
    })
    return () => cancelAnimationFrame(raf1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => { paint(daRef.current) }, [dayCount])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  const rebuildDays = useCallback(() => {
    const m = idxOf(moRef.current)
    const y = YEARS[idxOf(yrRef.current)]
    const max = daysInMonth(m, y)
    setDayCount(max)
    if (daRef.current && idxOf(daRef.current) > max - 1) {
      daRef.current.scrollTop = (max - 1) * H
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [YEARS])

  const onScroll = (el, settleFn) => {
    paint(el)
    if (refusal) setRefusal('')
    if (settleFn) {
      clearTimeout(el._dobT)
      el._dobT = setTimeout(settleFn, 110)
    }
  }

  const commit = () => {
    const m = idxOf(moRef.current) + 1
    const d = idxOf(daRef.current) + 1
    const y = YEARS[idxOf(yrRef.current)]
    const iso = `${y}-${pad2(m)}-${pad2(d)}`
    if (minAge > 0 && ageVerdict(iso, minAge) !== 'ok') {
      setRefusal(t('dob.tooYoung', { defaultValue: 'You must be {{age}} or older to use Orella.', age: minAge }))
      return
    }
    onChange && onChange(iso)
    setOpen(false)
  }

  const days = Array.from({ length: dayCount }, (_, i) => i + 1)

  return (
    <div>
      {label && <label className="block text-[12px] text-gray-500 mb-1">{label}</label>}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between bg-white"
        style={{ height: 42, border: '1.5px solid #E2E8F0', borderRadius: 12, padding: '0 12px' }}
      >
        <span style={{ fontSize: 14, color: display ? '#15354F' : '#94A3B8', fontWeight: display ? 600 : 400 }}>
          {display || placeholder}
        </span>
        <Calendar size={17} style={{ color: '#46A8F9' }} />
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(15,31,46,0.32)', display: 'flex', alignItems: 'flex-end' }}
        >
          <style>{`
            .dobcol{height:170px;overflow-y:scroll;scroll-snap-type:y mandatory;scrollbar-width:none;text-align:center;}
            .dobcol::-webkit-scrollbar{display:none;}
            .dobitem{height:34px;line-height:34px;scroll-snap-align:center;font-size:14px;color:#AFC6DA;font-weight:500;transition:color .12s,font-size .12s;white-space:nowrap;}
            .dobitem.sel{color:#15354F;font-weight:700;font-size:17px;}
            .dobitem.adj{color:#6E93B4;font-size:15px;}
          `}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', background: '#fff', borderRadius: '20px 20px 0 0', padding: '14px 16px calc(16px + env(safe-area-inset-bottom))' }}
          >
            <div style={{ width: 38, height: 4, borderRadius: 99, background: '#D8E6F0', margin: '0 auto 12px' }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: '#15354F', textAlign: 'center', marginBottom: 12 }}>
              {label || 'Date of birth'}
            </div>
            <div style={{ position: 'relative', height: 170 }}>
              <div style={{ position: 'absolute', left: 0, right: 0, top: 68, height: 34, background: '#EAF6FF', borderTop: '1.5px solid #A7D9FC', borderBottom: '1.5px solid #A7D9FC', borderRadius: 10, pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 56, background: 'linear-gradient(#fff,rgba(255,255,255,0))', pointerEvents: 'none', zIndex: 2 }} />
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 56, background: 'linear-gradient(rgba(255,255,255,0),#fff)', pointerEvents: 'none', zIndex: 2 }} />
              <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>
                <div ref={moRef} className="dobcol" style={{ flex: 1.5 }} onScroll={(e) => onScroll(e.currentTarget, rebuildDays)}>
                  <div style={{ height: 68 }} />
                  {MONTHS.map((m, i) => <div key={i} data-dobitem className="dobitem">{m}</div>)}
                  <div style={{ height: 68 }} />
                </div>
                <div ref={daRef} className="dobcol" style={{ flex: 0.9 }} onScroll={(e) => onScroll(e.currentTarget, null)}>
                  <div style={{ height: 68 }} />
                  {days.map((d) => <div key={d} data-dobitem className="dobitem">{d}</div>)}
                  <div style={{ height: 68 }} />
                </div>
                <div ref={yrRef} className="dobcol" style={{ flex: 1.1 }} onScroll={(e) => onScroll(e.currentTarget, rebuildDays)}>
                  <div style={{ height: 68 }} />
                  {YEARS.map((y) => <div key={y} data-dobitem className="dobitem">{y}</div>)}
                  <div style={{ height: 68 }} />
                </div>
              </div>
            </div>
            {refusal && (
              <p role="alert" style={{ fontSize: 12.5, color: '#DC2626', textAlign: 'center', marginTop: 10 }}>
                {refusal}
              </p>
            )}
            <button
              type="button"
              onClick={commit}
              style={{ width: '100%', marginTop: 14, height: 46, border: 'none', borderRadius: 13, background: '#15354F', color: '#fff', fontSize: 14.5, fontWeight: 700 }}
            >
              {t('common.done')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
